import { useState, useEffect } from 'react';
import { Flame, Snowflake, Share2 } from 'lucide-react';
import { api } from '@/lib/api';
import { trackPollVote, trackPollShare } from '@/lib/analytics';

interface HotTakePollProps {
  postId: string;
}

function getVisitorId(): string {
  const key = 'mw_visitor_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

export function HotTakePoll({ postId }: HotTakePollProps) {
  const [hot, setHot] = useState(0);
  const [cold, setCold] = useState(0);
  const [userVote, setUserVote] = useState<'hot' | 'cold' | null>(null);
  const [loading, setLoading] = useState(false);

  const total = hot + cold;
  const hotPct = total > 0 ? Math.round((hot / total) * 100) : 0;

  useEffect(() => {
    const visitorId = getVisitorId();
    api.reactions.getHotTake(postId, visitorId).then((data) => {
      setHot(data.hot);
      setCold(data.cold);
      setUserVote(data.userVote as 'hot' | 'cold' | null);
    }).catch(() => {
      // silently ignore — poll is non-critical
    });
  }, [postId]);

  const vote = async (voteType: 'hot' | 'cold', e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (userVote || loading) return;

    setLoading(true);
    // Optimistic update
    setUserVote(voteType);
    if (voteType === 'hot') setHot((h) => h + 1);
    else setCold((c) => c + 1);

    try {
      const visitorId = getVisitorId();
      const data = await api.reactions.castHotTake(postId, voteType, visitorId);
      setHot(data.hot);
      setCold(data.cold);
      setUserVote(data.userVote as 'hot' | 'cold');
      trackPollVote(postId, voteType);
    } catch {
      // Revert on error
      setUserVote(null);
      if (voteType === 'hot') setHot((h) => h - 1);
      else setCold((c) => c - 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={(e) => vote('hot', e)}
        disabled={!!userVote || loading}
        className={`relative z-10 flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold transition-all duration-200 active:scale-95 disabled:cursor-default ${
          userVote === 'hot'
            ? 'bg-orange-500/20 text-orange-400 ring-1 ring-orange-500/40'
            : userVote
              ? 'text-text-tertiary/50 cursor-default'
              : 'text-text-tertiary hover:bg-orange-500/10 hover:text-orange-400'
        }`}
      >
        <Flame className="h-3.5 w-3.5" />
        <span>Hot Take</span>
      </button>

      <button
        onClick={(e) => vote('cold', e)}
        disabled={!!userVote || loading}
        className={`relative z-10 flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold transition-all duration-200 active:scale-95 disabled:cursor-default ${
          userVote === 'cold'
            ? 'bg-sky-500/20 text-sky-400 ring-1 ring-sky-500/40'
            : userVote
              ? 'text-text-tertiary/50 cursor-default'
              : 'text-text-tertiary hover:bg-sky-500/10 hover:text-sky-400'
        }`}
      >
        <Snowflake className="h-3.5 w-3.5" />
        <span>Nah</span>
      </button>

      {userVote && total > 0 && (
        <>
          <span className="text-[11px] text-text-tertiary tabular-nums">
            {hotPct}% Hot · {total} vote{total !== 1 ? 's' : ''}
          </span>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const text = `${hotPct}% Hot Take · ${total} votes`;
              const url = `${window.location.origin}/post/${postId}`;
              if (navigator.share) {
                navigator.share({ text, url }).then(() => {
                  trackPollShare(postId, userVote, 'native');
                }).catch(() => { /* user cancelled */ });
              } else {
                navigator.clipboard.writeText(`${text} — ${url}`).catch(() => {});
                trackPollShare(postId, userVote, 'clipboard');
              }
            }}
            className="relative z-10 flex items-center gap-0.5 rounded-lg px-1.5 py-1 text-xs text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary transition-colors"
            aria-label="Share poll results"
          >
            <Share2 className="h-3 w-3" />
          </button>
        </>
      )}
    </div>
  );
}
